/**
 * /payroll/approvals/:timesheetId — one timesheet on its own page.
 *
 * This was the panel beside the approval queue. Split panes are banned
 * (CLAUDE.md, "Things we deliberately do not do"), so a queue row now opens
 * here, and Edit / Reject / Approve sit on the band, top right.
 *
 * CASING IS LIVE ON THIS SCREEN (TNT, PAR, Dash, Dutch). Nothing about a
 * decision changed in the move: the same three client calls with the same
 * bodies (`api.timesheets.approve(id)` or `(id, confirmation)`,
 * `api.timesheets.reject(id, { reason, note? })`, and the correction inside
 * TimesheetDetailModal), the same refusal wording, the same pay-notice toast,
 * the same 409 long-shift choice, the same invalidations. The suites under
 * ApprovalQueueScreen.*.test.tsx pin each of those against this page.
 *
 * WHAT DID HAVE TO BE DECIDED: where an approve goes. In the panel it moved the
 * selection to the next row of the list (fe#750). Here it opens the next row's
 * page, in the list the reader came from — its tab, date chip and name search
 * travel in `location.state.listUrl` (recordPage.ts) and are re-derived with
 * the list's own functions (approvalQueueModel.ts). The navigation REPLACES
 * this entry, so the back arrow and the browser's Back both land on the list,
 * not on a shift that has already been signed for. With nothing left, it
 * returns to the list.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import TimesheetDetailModal from '../../components/payroll/TimesheetDetailModal';
import { approvalPayNotice } from '../../constants/shop-pay';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { longShiftRefusal, type LongShift, type LongShiftConfirmation } from '../../lib/long-shift';
import { canAdminEditTimesheet, canReopenPayPeriod, getRoleLabel } from '../../lib/roles';
import { useBackToList } from '../../pages/downhole/recordPage';
import {
  APPROVAL_CONFLICT_MESSAGES,
  QUEUE_PATH,
  hoursText,
  queueRows,
  readQueueParams,
  rowAfter,
  useApproverAccess,
  useTimesheetQueue,
  type Timesheet,
} from './approvalQueueModel';

interface RecordState {
  listUrl?: unknown;
  /** The previous record's "… approved — 8.00h · 3 remaining", carried across the replace. */
  flash?: unknown;
}

function SuccessBanner({ message }: { message: string }) {
  return (
    <div
      role="status"
      className="flex items-center gap-2 px-4 py-3 rounded-fi-panel text-sm font-medium border border-fi-green/30 bg-fi-green/10 text-fieldiq-text"
    >
      <CheckCircle2 size={16} />
      {message}
    </div>
  );
}

/**
 * Rendered where the Approve and Reject buttons would be. A button that simply
 * vanishes reads as a bug and gets reported as one; saying why costs one line.
 */
function NoApproverAccessNotice({ role }: { role?: string }) {
  return (
    <div className="flex items-start gap-2 px-4 py-3 rounded-fi-panel text-sm border border-fi-amber/30 bg-fi-amber/10 text-fieldiq-accent-signal">
      <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
      <span>
        You can review these hours, but approving them needs an approver role
        {role ? ` — yours is ${getRoleLabel(role)}` : ''}. Approval is what lets a
        long shift count toward reported safety hours, so it takes a second
        person.
      </span>
    </div>
  );
}

export default function TimesheetApprovalScreen() {
  const { timesheetId = '' } = useParams<{ timesheetId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const timeZone = useTenantTimeZone();
  const { user } = useAuth();
  const { canApprove } = useApproverAccess();
  const back = useBackToList(QUEUE_PATH);

  const state = (location.state as RecordState | null) ?? null;
  const listUrl = typeof state?.listUrl === 'string' && state.listUrl.startsWith(QUEUE_PATH) ? state.listUrl : QUEUE_PATH;
  const listParams = useMemo(
    () => readQueueParams(new URLSearchParams(listUrl.split('?')[1] ?? '')),
    [listUrl],
  );

  /*
    The banner from the approve that brought the reader here, for five seconds,
    as the panel showed it. Keyed on the history entry, so it neither survives
    onto a later record nor reappears on a re-render.
  */
  const [flash, setFlash] = useState<string | null>(null);
  useEffect(() => {
    const msg = typeof state?.flash === 'string' ? state.flash : null;
    setFlash(msg);
    if (!msg) return undefined;
    const t = setTimeout(() => setFlash(null), 5000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  /* The list this record was opened from, for "next" and "N remaining". */
  const { data: rawTimesheets = [] } = useTimesheetQueue();
  const { tabRows, rows } = useMemo(
    () => queueRows(rawTimesheets as Timesheet[], listParams, timeZone),
    [rawTimesheets, listParams, timeZone],
  );
  /* `tabRows`, not `rows`: a name search must not make "0 remaining" true. */
  const pendingCount = tabRows.filter((t) => t.status === 'submitted').length;

  /* Same key as TimesheetDetailModal's read — one request, one cache entry. */
  const { data: record } = useQuery<Timesheet>({
    queryKey: ['timesheet', timesheetId],
    queryFn: () => api.timesheets.get(timesheetId).then((r) => r.data?.data ?? r.data),
    enabled: !!timesheetId,
  });

  /* The band's slot for Edit / Reject / Approve, handed to the detail. */
  const [bandActions, setBandActions] = useState<HTMLElement | null>(null);

  const goNext = useCallback(
    (fromId: string, msg?: string) => {
      const next = rowAfter(rows, fromId);
      if (next && next.id !== fromId) {
        navigate(`${QUEUE_PATH}/${next.id}`, { replace: true, state: { listUrl, flash: msg } });
      } else {
        // Nothing left to sign: the list, with the message the panel would have shown.
        navigate(listUrl, { replace: true, state: msg ? { flash: msg } : undefined });
      }
    },
    [rows, listUrl, navigate],
  );

  /**
   * Shared by approve and reject. Reads BOTH server envelopes:
   *   409 attestation  { success: false, error, code }   — timesheets.routes.ts
   *   403 requireRole  { error }                          — middleware/auth.ts
   */
  const showError = useCallback((err: any, fallback: string) => {
    const code = err?.response?.data?.code;
    const serverMsg = err?.response?.data?.error;
    toast.error((code && APPROVAL_CONFLICT_MESSAGES[code]) || serverMsg || fallback);
  }, []);

  /**
   * The 409 `long_shift_unconfirmed` for the row it refused, handed to the
   * detail so it opens the same Fix / Confirm choice a flagged row gets.
   */
  const [longShiftRefused, setLongShiftRefused] = useState<{ id: string; longShift: LongShift } | null>(null);

  const approveMutation = useMutation({
    mutationFn: ({ id, confirmation }: { id: string; confirmation?: LongShiftConfirmation }) =>
      confirmation ? api.timesheets.approve(id, confirmation) : api.timesheets.approve(id),
    onMutate: () => setLongShiftRefused(null),
    onSuccess: (res: any, { id }) => {
      const ts = rows.find((t) => t.id === id) ?? (record?.id === id ? record : undefined);
      const remaining = pendingCount - 1;
      // The approve stood, but it may have filed no pay — for a US hourly hand
      // (api#1097) a field shift IS their pay. A warning, not an error.
      const payNotice = approvalPayNotice(res?.data?.shop_pay);
      if (payNotice) toast.warning(payNotice, { duration: 12000 });
      // No dollar figure: approving a clock-in record approves HOURS.
      goNext(id, `${ts?.worker_name ?? 'Timesheet'} approved — ${hoursText(ts)} · ${remaining} remaining`);
    },
    onError: (err: any, { id }) => {
      // Not a toast: the detail opens the choice the refusal is asking for.
      const refused = longShiftRefusal(err);
      if (refused) { setLongShiftRefused({ id, longShift: refused }); return; }
      showError(err, 'Could not approve this timesheet.');
    },
    // onSettled: a refused approve still re-reads, so the list agrees with the server.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason, note }: { id: string; reason: string; note?: string }) =>
      // A blank note is absent, not '' (api#981 stores trimmed-or-null).
      api.timesheets.reject(id, { reason, ...(note?.trim() ? { note: note.trim() } : {}) }),
    onSuccess: (_data, { id }) => goNext(id),
    // No attestation code can come back from a reject; this catches the 403
    // and the 400 for a missing reason.
    onError: (err: any) => showError(err, 'Could not reject this timesheet.'),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });

  /*
    Approve and Reject on a SUBMITTED row, for a role the server will let sign.
    A draft is not `submitted`, so POST /approve would 404 on it; an approved or
    rejected row has nothing left to decide. The correction is offered
    separately, on every status — approved rows are the point of it.
  */
  const isSubmitted = record?.status === 'submitted';
  const context = isSubmitted && canApprove ? 'approval' : 'readonly';

  return (
    <div data-testid="timesheet-record-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      {/* The app shell renders the one global <Header />. Pages never do. */}
      <PageHeaderBand
        eyebrow="Timesheet"
        title={record?.worker_name || 'Timesheet'}
        onBack={back}
        backLabel="Back to timesheets"
        actions={<div ref={setBandActions} className="flex flex-wrap items-center gap-2" />}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {flash && <SuccessBanner message={flash} />}
          {isSubmitted && !canApprove && <NoApproverAccessNotice role={user?.role} />}

          <div
            data-testid="approval-queue-detail"
            className="flex flex-col rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            <TimesheetDetailModal
              key={timesheetId}
              timesheetId={timesheetId}
              context={context}
              canAdminEdit={canAdminEditTimesheet(user?.role)}
              canReopenPeriod={canReopenPayPeriod(user?.role)}
              bandActions={bandActions}
              scrollsWithPage
              onClose={back}
              onApprove={(id, confirmation) => approveMutation.mutate({ id, confirmation })}
              approving={approveMutation.isPending}
              longShiftRefused={longShiftRefused}
              onReject={(id, reason, note) => rejectMutation.mutate({ id, reason, note })}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
